import {
  GraphFrameExportResponseSchema,
  GraphFrameJsonExportArtifactSchema,
  GraphFrameJsonImportRequestSchema,
  GraphFrameObsidianExportArtifactSchema,
  GraphFrameObsidianImportRequestSchema,
  GraphVisualizationFrameSchema,
  type GraphFrameJsonImportRequest,
  type GraphFrameObsidianImportRequest,
  type GraphObsidianExportFile
} from "@salesko/contracts";

const maxJsonImportBytes = 5_000_000;

export type GraphFrameFileImportMessages = {
  jsonTooLarge: string;
  jsonInvalid: (fileName: string) => string;
  jsonInvalidShape: string;
  obsidianEmptySelection: string;
  obsidianJsonTooLarge: string;
  obsidianJsonInvalidShape: string;
  obsidianMarkdownTooLarge: (fileName: string) => string;
};

export type GraphFrameFileImportOptions = {
  messages: GraphFrameFileImportMessages;
  sessionId?: string;
  submittedAt?: string;
};

export async function jsonGraphFrameImportRequestFromFile(
  file: File,
  options: GraphFrameFileImportOptions
): Promise<GraphFrameJsonImportRequest> {
  const { messages, ...requestOptions } = options;
  if (file.size > maxJsonImportBytes) {
    throw new Error(messages.jsonTooLarge);
  }

  const payload = parseJson(await file.text(), file.name, messages);
  const artifactCandidate = artifactPayload(payload);
  const artifact = GraphFrameJsonExportArtifactSchema.safeParse(artifactCandidate);
  if (artifact.success) {
    return GraphFrameJsonImportRequestSchema.parse({
      ...requestOptions,
      message: importMessage("JSON", file.name),
      artifact: artifact.data
    });
  }

  const frameCandidate = framePayload(payload);
  const frame = GraphVisualizationFrameSchema.safeParse(frameCandidate);
  if (frame.success) {
    return GraphFrameJsonImportRequestSchema.parse({
      ...requestOptions,
      message: importMessage("JSON", file.name),
      frame: frame.data
    });
  }

  throw new Error(messages.jsonInvalidShape);
}

export async function obsidianGraphFrameImportRequestFromFiles(
  files: File[],
  options: GraphFrameFileImportOptions
): Promise<GraphFrameObsidianImportRequest> {
  const { messages, ...requestOptions } = options;
  if (files.length === 0) {
    throw new Error(messages.obsidianEmptySelection);
  }

  const exportArtifact = files.length === 1 && isJsonFile(files[0]) ? await obsidianExportArtifactFromJson(files[0], messages) : null;
  const graphFiles = exportArtifact?.files ?? (await Promise.all(files.map((file) => markdownFileFromBrowserFile(file, messages))));
  const request = {
    ...requestOptions,
    message: importMessage("Obsidian", fileListLabel(files)),
    files: graphFiles
  };

  return GraphFrameObsidianImportRequestSchema.parse(request);
}

function parseJson(text: string, fileName: string, messages: GraphFrameFileImportMessages) {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new Error(messages.jsonInvalid(fileName));
  }
}

function artifactPayload(payload: unknown) {
  const exportResponse = GraphFrameExportResponseSchema.safeParse(payload);
  if (exportResponse.success && exportResponse.data.format === "json") {
    return exportResponse.data.artifact;
  }

  if (payload && typeof payload === "object" && "artifact" in payload) {
    return (payload as { artifact?: unknown }).artifact;
  }

  return payload;
}

function framePayload(payload: unknown) {
  if (payload && typeof payload === "object" && "frame" in payload) {
    return (payload as { frame?: unknown }).frame;
  }

  return payload;
}

async function obsidianExportArtifactFromJson(file: File, messages: GraphFrameFileImportMessages) {
  if (file.size > maxJsonImportBytes) {
    throw new Error(messages.obsidianJsonTooLarge);
  }

  const payload = parseJson(await file.text(), file.name, messages);
  const exportResponse = GraphFrameExportResponseSchema.safeParse(payload);
  const artifactCandidate =
    exportResponse.success && exportResponse.data.format === "obsidian"
      ? exportResponse.data.artifact
      : payload && typeof payload === "object" && "artifact" in payload
        ? (payload as { artifact?: unknown }).artifact
        : payload;
  const artifact = GraphFrameObsidianExportArtifactSchema.safeParse(artifactCandidate);
  if (!artifact.success) {
    throw new Error(messages.obsidianJsonInvalidShape);
  }

  return artifact.data;
}

async function markdownFileFromBrowserFile(file: File, messages: GraphFrameFileImportMessages): Promise<GraphObsidianExportFile> {
  if (file.size > 100_000) {
    throw new Error(messages.obsidianMarkdownTooLarge(file.name));
  }

  const content = await file.text();
  return {
    path: browserFilePath(file),
    content
  };
}

function browserFilePath(file: File) {
  return file.webkitRelativePath || file.name;
}

function isJsonFile(file: File | undefined): file is File {
  if (!file) {
    return false;
  }

  return file.type === "application/json" || file.name.toLowerCase().endsWith(".json");
}

function fileListLabel(files: File[]) {
  if (files.length === 1) {
    return files[0]?.name ?? "file";
  }

  return `${files.length} files`;
}

function importMessage(format: "JSON" | "Obsidian", fileName: string) {
  return `${format} import: ${fileName}`.slice(0, 500);
}
